import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { DetailSearchParams } from 'entities/kyrgyzstan/model'

import { BuyTicketApi } from 'shared/api/BuyTicket'
import {
  KyrgyzstanOrdersApi,
  type PaymentStatus,
} from 'shared/api/kyrgyzstan/orders'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'

import type { BookingFormValues } from '../model'

import { getBookingErrorMessage } from './get-booking-error-message'
import {
  type BookingSubmitParams,
  getBookingSubmitParams,
} from './get-booking-submit-params'

interface UseSubmitBookingParams {
  onError: (errorText?: string) => void
}

interface SubmitBookingOptions {
  formData: BookingFormValues
  realtyId?: string
  detailSearchParams?: DetailSearchParams | null
  selectedPayment?: PaymentStatus
  ratehawkBookHash?: string | null
}

const BUY_TICKET_TYPE_KYRGYZSTAN_ORDERS = 'kyrgyzstan_orders'

const checkPersonalData = async ({
  personalDataPayload,
}: BookingSubmitParams) => {
  const checkResponse =
    await KyrgyzstanOrdersApi.checkPersonalData(personalDataPayload)
  const { valid, errors } = checkResponse.data.data

  if (!valid) {
    throw new Error(errors.join(', '))
  }
}

const createOrder = async ({ orderPayload }: BookingSubmitParams) => {
  const response = await KyrgyzstanOrdersApi.createOrder(orderPayload)
  const orderId = response.data.data.id

  if (!orderId) {
    throw new Error()
  }

  return orderId
}

export const useSubmitBooking = ({ onError }: UseSubmitBookingParams) => {
  const { t } = useTranslation()
  const onErrorRef = useRef(onError)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    onErrorRef.current = onError
  }, [onError])

  const submitBooking = useCallback(
    async (options: SubmitBookingOptions) => {
      const params = getBookingSubmitParams(options)

      if (!params) {
        onErrorRef.current(t('kyrgyzstan_block.booking.errors.missing_data'))
        return
      }

      setIsLoading(true)

      try {
        await checkPersonalData(params)

        const orderId = await createOrder(params)

        const { data } = await BuyTicketApi.buyTicketConfirm({
          id: orderId,
          type: BUY_TICKET_TYPE_KYRGYZSTAN_ORDERS,
        })

        await startPaymentFromConfirmResult(data)
      } catch (error) {
        onErrorRef.current(getBookingErrorMessage(error, t))
      } finally {
        setIsLoading(false)
      }
    },
    [t],
  )

  return {
    submitBooking,
    isLoading,
  }
}
